import React, { Component } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loading } from 'components/cosmos';
import { Form, FormControl } from 'components/form';
import { PanelCheckList } from 'components/panelCheckList';
import { Toggle } from 'components/toggle';
import { InfoTooltip } from 'components/tooltip';
import { I18n, Polyglot } from 'components/translate';
import Settings from 'configs/Settings';
import Currency from 'helpers/Currency';
import intersectionWith from 'lodash/intersectionWith';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { GROUPS, NAMES } from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { ACTIONS, CATEGORIES, EVENTS } from 'modules/app/services/ga/constants';
import { AuthService } from 'modules/auth';
import { ContentBasePage } from 'modules/content';
import { MembershipStore } from 'modules/membership';
import PropTypes from 'prop-types';

import SalesGroupModal from './SalesGroupModal';

import SalesService from '../SalesService';

import './SalesGroup.scss';

@inject((stores) => ({
  SalesStore: stores.SalesStore,
  isAccessPlusEnabled: stores.MembershipStore.isAccessPlusEnabled,
  isMobile: stores.UiStore.isMobile,
  productId: stores.MembershipStore.productId,
  subscription: stores.MembershipStore.subscription
}))
@observer
export default class SalesGroup extends Component {
  static propTypes = {
    SalesStore: MobxPropTypes.objectOrObservableObject,
    isAccessPlusEnabled: PropTypes.bool,
    isMobile: PropTypes.bool,
    fixedBarId: PropTypes.string,
    tabIndex: PropTypes.number,
    productId: PropTypes.number,
    subscription: PropTypes.bool
  };

  state = {
    loading: true,
    membershipOffers: [],
    initialSelectedOffers: [],
    groupSelectedOffers: [],
    showSalesGroupModal: false,
    isAccessPlusEnabled: false,
    formData: {},
    modalType: ''
  };

  async componentDidMount() {
    this.setState({ isAccessPlusEnabled: this.props.isAccessPlusEnabled });
    await this.fetchData();
  }

  enableLoader() {
    this.setState({ loading: true });
  }

  disableLoader() {
    this.setState({ loading: false });
  }

  closeSalesGroupModal = () => {
    this.setState({ showSalesGroupModal: false });
  };

  showSalesGroupModalByType = (modalType) => {
    this.setState({ modalType, showSalesGroupModal: true });
  };

  async fetchData() {
    const membershipOffers = this.props.SalesStore.offers;
    const groupSelectedOffers =
      await SalesService.fetchMembershipGroupSelectedOffers();
    const availableOffers = intersectionWith(
      groupSelectedOffers,
      membershipOffers,
      SalesService.isOfferMatch
    );
    const offerKeys = availableOffers.map((offer) => offer.offerKey);

    this.setState({
      membershipOffers,
      initialSelectedOffers: Object.assign([], offerKeys),
      groupSelectedOffers: offerKeys
    });
    this.disableLoader();
  }

  formatItems = (offers = [], value) => {
    const { membershipOffers } = this.state;
    const hashList = value || offers;

    const items =
      membershipOffers &&
      membershipOffers.map((offer) => {
        offer.name = offer.activationCode || Polyglot.t('general.default');
        offer.value = offer.key;
        offer.labelValue = offer.activationCode;
        offer.checked = hashList.includes(offer.key);

        return offer;
      });

    return items;
  };

  formatItemMobile = (item) => {
    return (
      <div className="radio__custom__label">
        <div>
          {item.activationCode ? (
            item.activationCode
          ) : (
            <I18n label="general.default" />
          )}
          <br />
          <span className="secondary">{item.key}</span>
        </div>
        <div>
          {item.price && item.currencyCode && (
            <div className="secondary">
              <I18n label="general.price" />
              {`: ${Currency.formatCurrencySymbol(
                parseFloat(item.price),
                item.currencyCode
              )}`}
            </div>
          )}
        </div>
      </div>
    );
  };

  formatItemDesktop = (item) => {
    return (
      <div className="check__custom__label">
        <span>
          {item.activationCode ? (
            item.activationCode
          ) : (
            <I18n label="general.default" />
          )}
          <br />
          <span className="secondary">{item.key}</span>
        </span>
        <span>
          {item.price &&
            item.currencyCode &&
            Currency.formatCurrencySymbol(
              parseFloat(item.price),
              item.currencyCode
            )}
        </span>
      </div>
    );
  };

  checkListHeader = () => {
    return (
      !this.props.isMobile && (
        <div className="checklist__header">
          <span>
            <I18n label="general.name_code" />
          </span>
          <span>
            <I18n label="general.price" />
          </span>
        </div>
      )
    );
  };

  getChangedOffers(newSelectedOffers) {
    if (newSelectedOffers.length !== this.state.initialSelectedOffers.length) {
      return true;
    }

    const hasChanges = !newSelectedOffers.every((item) =>
      this.state.initialSelectedOffers.includes(item)
    );

    return hasChanges;
  }

  saveHandler = (formData) => {
    if (!formData.offersCheckList) {
      formData.offersCheckList = this.state.groupSelectedOffers;
    }

    this.setState({ formData });
    const changedOffers = this.getChangedOffers(formData.offersCheckList);

    if (changedOffers) {
      this.showSalesGroupModalByType('change');
    } else {
      this.confirmSave();
    }
  };

  confirmSave = async () => {
    const { formData } = this.state;

    if (!formData.offersCheckList) {
      formData.offersCheckList = this.state.groupSelectedOffers;
    }

    formData.accessPlusToggle = this.state.isAccessPlusEnabled;
    this.closeSalesGroupModal();
    this.enableLoader();
    try {
      await SalesService.updateGroupsInfo(formData);
      const offers = Object.assign([], formData.offersCheckList);

      this.setState({
        initialSelectedOffers: offers,
        groupSelectedOffers: offers
      });
    } finally {
      this.disableLoader();
    }
  };

  onCancelToggleModal = () => {
    this.setState(
      { isAccessPlusEnabled: !this.state.isAccessPlusEnabled },
      () => {
        this.closeSalesGroupModal();
      }
    );
  };

  onChangeToggle() {
    const { isAccessPlusEnabled } = this.state;
    const modalType = isAccessPlusEnabled ? 'disable' : 'activate';

    this.setState({ isAccessPlusEnabled: !isAccessPlusEnabled }, () => {
      this.showSalesGroupModalByType(modalType);
    });

    this.registerMetrics('CLICK_TOGGLE');
  }

  get offerLink() {
    const { productId, subscription } = this.props;

    return subscription
      ? `${Settings.app.bw}/products/manage/${productId}/subscriptions/plans`
      : `${Settings.app.bw}/products/manage/${productId}/offers`;
  }

  registerMetrics(action, actionDetail = false) {
    const { marketplaceId } = MembershipStore;

    if (marketplaceId) {
      let gtmAction = ACTIONS.PLUS_ACCESS[action];
      const capitalizeWord = (word) =>
        word.charAt(0).toUpperCase() + word.slice(1);

      const DATAHUB_EVENT = {
        group: GROUPS.PLUS_ACCESS,
        name: NAMES.PLUS_ACCESS[action],
        user: { id: marketplaceId }
      };

      if (actionDetail) {
        DATAHUB_EVENT.params = { order: actionDetail };

        actionDetail = capitalizeWord(actionDetail);
        gtmAction = `${ACTIONS.PLUS_ACCESS[action]}:${actionDetail}`;
      }

      const GTM_EVENT = {
        event: EVENTS.INTERACTION,
        event_category: CATEGORIES.CLUB_ADMIN_CONTENT,
        event_action: gtmAction,
        event_label: true
      };

      try {
        window.dataLayer.push(GTM_EVENT);
        DatahubService.sendMetrify(DATAHUB_EVENT);
      } catch (error) {
        // do nothing
      }
    }
  }

  render() {
    const {
      isAccessPlusEnabled,
      loading,
      groupSelectedOffers,
      showSalesGroupModal,
      modalType,
      ...props
    } = this.state;

    const onCancel = () => {
      const metricEventName =
        modalType === 'activate'
          ? 'MODAL_ACTIVATE_CANCEL'
          : 'MODAL_DEACTIVATE_CANCEL';

      this.registerMetrics(metricEventName);
      return modalType === 'change'
        ? this.closeSalesGroupModal()
        : this.onCancelToggleModal();
    };

    const onConfirm = () => {
      const metricEventName =
        modalType === 'activate'
          ? 'MODAL_ACTIVATE_CONFIRM'
          : 'MODAL_DEACTIVATE_CONFIRM';

      this.registerMetrics(metricEventName);
      return modalType === 'change'
        ? this.confirmSave()
        : this.closeSalesGroupModal();
    };

    if (loading) {
      return <Loading center />;
    }

    const isOwner = AuthService.isOwner();

    return (
      <Form
        className="sales_group"
        ref={(node) => (this.formNode = node)}
        onSubmit={this.saveHandler}
      >
        <div className="grid grid__block">
          <div className="card__description">
            <h2>
              <I18n label="sales_manager.access_plus" />
            </h2>
            <p>
              <I18n label="sales_manager.sales_group.description" />
            </p>
            <p>
              <I18n label="sales_manager.sales_group.add_new_offers" />
            </p>
          </div>

          <div className="sales__toggle__wrapper">
            <div className="sales_toggle__wrapper__label">
              <label>
                <I18n label="sales_manager.sales_group.activate_offers_toggle_text" />
              </label>
              <InfoTooltip
                rerenderPosition={this.props.tabIndex === 1}
                text="sales_manager.sales_group.activate_offers_tooltip"
              />
            </div>

            {
              <Toggle
                name="accessPlusToggle"
                value={isAccessPlusEnabled}
                id="accessPlusToggle"
                checked={isAccessPlusEnabled}
                onChange={() => this.onChangeToggle()}
              />
            }
          </div>

          <label id="choose_offer">
            <I18n label="sales_manager.sales_group.choose_offer" />
          </label>

          <FormControl>
            <PanelCheckList
              {...props}
              disabled={!isAccessPlusEnabled}
              panelTitle="sales_manager.sales_group.access_plus_offers"
              name="offersCheckList"
              value={groupSelectedOffers}
              formatItems={this.formatItems}
              formatItem={
                this.props.isMobile
                  ? this.formatItemMobile
                  : this.formatItemDesktop
              }
              checkListHeader={this.checkListHeader}
            />
          </FormControl>

          {isOwner && (
            <p
              data-test="sales_group-external"
              className="sales_group__external"
            >
              <span className="sales_group__external__question">
                <I18n label="sales_manager.sales_group.precification.question" />{' '}
              </span>
              <I18n label="sales_manager.sales_group.precification.answer" />{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={this.offerLink}
                className="_text-blue-dark"
              >
                <I18n label="sales_manager.sales_info.price.info_link" />
                <FontAwesomeIcon
                  icon={['fal', 'external-link']}
                  className="_ml-2"
                />
              </a>
            </p>
          )}
        </div>

        <ContentBasePage.Footer onlySave />

        {showSalesGroupModal && (
          <SalesGroupModal
            type={modalType}
            onCancel={onCancel}
            onConfirm={onConfirm}
            registerMetrics={this.registerMetrics}
          />
        )}
      </Form>
    );
  }
}
